'use client'

import { Badge } from '@brew/ui/badge'
import { cn } from '@brew/ui/cn'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { appToast } from '@/lib/shared/toast'

type Visibility = 'public' | 'private'

interface VisibilityToggleBadgeProps {
  emailId: string
  currentVisibility: string
}

/** In menu order; each is also its label's key (`templates.visibility`). */
const VISIBILITY_OPTIONS: ReadonlyArray<Visibility> = ['public', 'private']

function normalizeVisibility(value: string): Visibility {
  return value === 'public' ? 'public' : 'private'
}

export function VisibilityToggleBadge({
  emailId,
  currentVisibility,
}: VisibilityToggleBadgeProps) {
  const t = useTranslations('templates')
  const router = useRouter()
  const [visibility, setVisibility] = useState<Visibility>(
    normalizeVisibility(currentVisibility)
  )
  const [isOpen, setIsOpen] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const handleSelect = async (newVisibility: Visibility) => {
    if (isPending || newVisibility === visibility) {
      setIsOpen(false)
      return
    }
    const previous = visibility
    setVisibility(newVisibility)
    setIsOpen(false)
    setIsPending(true)
    try {
      const response = await fetch(`/api/templates/${emailId}/visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility: newVisibility }),
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string
        }
        setVisibility(previous)
        appToast.error(payload.error ?? t('visibility.failed'))
        return
      }
      // Re-stream the RSC tree so the page's `currentVisibility` prop and
      // any downstream caches reflect the new server state seamlessly.
      router.refresh()
    } catch {
      setVisibility(previous)
      appToast.error(t('visibility.failed'))
    } finally {
      setIsPending(false)
    }
  }

  const isPublic = visibility === 'public'

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="cursor-pointer"
        disabled={isPending}
      >
        <Badge
          variant="outline"
          className={cn({
            'border-emerald-500/40 bg-emerald-500/10 text-emerald-600':
              isPublic,
            'border-border text-muted-foreground': !isPublic,
          })}
        >
          {t(`visibility.${visibility}`)}
        </Badge>
      </button>

      {isOpen ? (
        <div className="absolute top-full right-0 z-50 mt-1 min-w-[120px] rounded-lg border border-border bg-popover p-1">
          {VISIBILITY_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleSelect(option)}
              className={cn(
                'w-full rounded-sm px-3 py-1.5 text-left text-xs transition-colors',
                {
                  'bg-muted font-medium': visibility === option,
                  'hover:bg-muted/50': visibility !== option,
                }
              )}
            >
              {t(`visibility.${option}`)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
